import React, { useContext, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../LayoutPage/Layout/Layout";
import { AuthContext } from "../../Logica/context/AuthContext";
import { authFetch } from "../../../utils/authFetch";
import { exportarRegistrados } from "../../../utils/dashboard/exportRegistradosExcel";
import { exportarReporteCompleto } from "../../../utils/dashboard/exportReporteCompletoExcel";
import { restarSeisHoras } from "../../../utils/fechaMexico";
import "./DashboardAdmin.css";

const TarjetaMetrica = ({ etiqueta, valor, detalle }) => (
  <div className="col-sm-6 col-lg-3 mb-3">
    <div className="card h-100 shadow-sm">
      <div className="card-body text-center">
        <div className="fs-3 fw-bold">{valor}</div>
        <div className="text-muted small">{etiqueta}</div>
        {detalle && <div className="text-muted" style={{ fontSize: "0.75rem" }}>{detalle}</div>}
      </div>
    </div>
  </div>
);

const BarraOcupacion = ({ porcentaje }) => (
  <div className="progress" style={{ height: "0.6rem", minWidth: "80px" }}>
    <div
      className={`progress-bar ${porcentaje >= 100 ? "bg-danger" : porcentaje >= 75 ? "bg-warning" : "bg-success"}`}
      role="progressbar"
      style={{ width: `${Math.min(porcentaje, 100)}%` }}
      aria-valuenow={porcentaje}
      aria-valuemin="0"
      aria-valuemax="100"
    />
  </div>
);

const DashboardAdmin = () => {
  const { usuario, estadoUsuario, userType } = useContext(AuthContext);
  const esAdmin = userType?.toLowerCase() === "admin";

  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const [busquedaMateria, setBusquedaMateria] = useState("");
  const [busquedaRegistrado, setBusquedaRegistrado] = useState("");
  const [seleccionados, setSeleccionados] = useState(() => new Set());
  const [generandoReporte, setGenerandoReporte] = useState(false);

  useEffect(() => {
    if (estadoUsuario !== "autorizado" || !esAdmin || !usuario?.email) return;

    const cargarDashboard = async () => {
      setCargando(true);
      setError(null);
      try {
        const respuesta = await authFetch(
          `https://api.micrositec.com/api/dashboard?email=${encodeURIComponent(usuario.email)}`,
        );
        const data = await respuesta.json();

        if (respuesta.ok && data.ok) {
          setDatos(data.data);
        } else {
          setError(data.message || "No se pudo cargar la información.");
        }
      } catch (err) {
        console.error("Error al cargar el dashboard:", err);
        setError("Error de red al cargar la información.");
      }
      setCargando(false);
    };

    cargarDashboard();
  }, [estadoUsuario, esAdmin, usuario]);

  const toggleSeleccion = (email) => {
    setSeleccionados((anteriores) => {
      const nuevos = new Set(anteriores);
      if (nuevos.has(email)) {
        nuevos.delete(email);
      } else {
        nuevos.add(email);
      }
      return nuevos;
    });
  };

  const toggleSeleccionarTodo = (filas) => {
    setSeleccionados((anteriores) => {
      const todosSeleccionados = filas.length > 0 && filas.every((r) => anteriores.has(r.email));
      const nuevos = new Set(anteriores);
      filas.forEach((r) => (todosSeleccionados ? nuevos.delete(r.email) : nuevos.add(r.email)));
      return nuevos;
    });
  };

  const handleDescargarReporte = async () => {
    const filas = datos.registrados.filter((r) => seleccionados.has(r.email));
    await exportarRegistrados(filas);
  };

  const handleDescargarReporteCompleto = async () => {
    setGenerandoReporte(true);
    try {
      await exportarReporteCompleto(datos);
    } finally {
      setGenerandoReporte(false);
    }
  };

  return (
    <Layout mostrarAside={false}>
      <div className="container mt-1">
        <Link to="/" className="btn btn-link ps-0 mb-2 text-decoration-none">
          ← Volver al inicio
        </Link>
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
          <h2 className="mb-0">Dashboard de administrador</h2>
          {esAdmin && datos && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleDescargarReporteCompleto}
              disabled={generandoReporte}
            >
              {generandoReporte ? "Generando…" : "Descargar reporte completo"}
            </button>
          )}
        </div>

        {estadoUsuario === "cargando" && <p>Cargando...</p>}

        {estadoUsuario === "autorizado" && !esAdmin && (
          <div className="alert alert-danger">No tienes permiso para ver esta página.</div>
        )}

        {estadoUsuario !== "autorizado" && estadoUsuario !== "cargando" && (
          <div className="alert alert-warning">Inicia sesión como administrador para ver esta página.</div>
        )}

        {esAdmin && cargando && <p>Cargando estadísticas...</p>}
        {esAdmin && error && <div className="alert alert-danger">{error}</div>}

        {esAdmin && datos && (
          <>
            <h5 className="mt-3">Resumen general</h5>
            <div className="row">
              <TarjetaMetrica etiqueta="Alumnos registrados" valor={datos.resumen.totalAlumnosRegistrados} />
              <TarjetaMetrica
                etiqueta="Usuarios con permiso"
                valor={datos.resumen.totalUsuariosConPermiso}
                detalle={`${datos.resumen.totalUsuariosPendientes} sin registrarse todavía`}
              />
              <TarjetaMetrica
                etiqueta="Lugares ocupados / totales"
                valor={`${datos.resumen.lugaresOcupados} / ${datos.resumen.capacidadTotal}`}
                detalle={`${datos.resumen.lugaresLibres} lugares libres`}
              />
              <TarjetaMetrica
                etiqueta="Ocupación general"
                valor={`${datos.resumen.porcentajeOcupacion}%`}
                detalle={`${datos.resumen.materiasAgotadas} materia(s) agotada(s)`}
              />
            </div>

            <h5 className="mt-4">Ocupación por categoría</h5>
            <div className="table-responsive">
              <table className="table table-sm table-striped align-middle">
                <thead>
                  <tr>
                    <th>Categoría</th>
                    <th># Materias</th>
                    <th>Ocupados</th>
                    <th>Libres</th>
                    <th>Capacidad</th>
                    <th>Ocupación</th>
                  </tr>
                </thead>
                <tbody>
                  {datos.porCategoria.map((c) => (
                    <tr key={c.category}>
                      <td>{c.category}</td>
                      <td>{c.materias}</td>
                      <td>{c.ocupados}</td>
                      <td>{c.libres}</td>
                      <td>{c.capacidad}</td>
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <BarraOcupacion porcentaje={c.porcentajeOcupacion} />
                          <span className="small">{c.porcentajeOcupacion}%</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <h5 className="mt-4">Ocupación por materia</h5>
            <input
              type="text"
              className="form-control mb-2"
              placeholder="Buscar por código o nombre..."
              value={busquedaMateria}
              onChange={(e) => setBusquedaMateria(e.target.value)}
            />
            {(() => {
              const filtro = busquedaMateria.trim().toLowerCase();
              const materiasFiltradas = filtro
                ? datos.porMateria.filter(
                    (m) =>
                      m.name?.toLowerCase().includes(filtro) || m.code?.toLowerCase().includes(filtro),
                  )
                : datos.porMateria;

              return (
                <div className="table-responsive" style={{ maxHeight: "420px", overflowY: "auto" }}>
                  <table className="table table-sm table-striped align-middle mb-0">
                    <thead style={{ position: "sticky", top: 0, zIndex: 1 }} className="table-light">
                      <tr>
                        <th>Código</th>
                        <th>Nombre</th>
                        <th>Categoría</th>
                        <th>Ocupados</th>
                        <th>Libres</th>
                        <th>Capacidad</th>
                        <th>Ocupación</th>
                        <th>Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {materiasFiltradas.map((m) => (
                        <tr key={m.id}>
                          <td>{m.code}</td>
                          <td>{m.name}</td>
                          <td>{m.category}</td>
                          <td>{m.ocupados}</td>
                          <td>{m.libres}</td>
                          <td>{m.capacidad}</td>
                          <td>
                            <div className="d-flex align-items-center gap-2">
                              <BarraOcupacion porcentaje={m.porcentajeOcupacion} />
                              <span className="small">{m.porcentajeOcupacion}%</span>
                            </div>
                          </td>
                          <td>
                            {m.agotada ? (
                              <span className="badge bg-danger">Agotada</span>
                            ) : (
                              <span className="badge bg-success">Disponible</span>
                            )}
                          </td>
                        </tr>
                      ))}
                      {materiasFiltradas.length === 0 && (
                        <tr>
                          <td colSpan={8} className="text-center text-muted">
                            No hay materias que coincidan con "{busquedaMateria}".
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              );
            })()}

            <h5 className="mt-4">Alumnos registrados</h5>
            <div className="d-flex gap-2 mb-2">
              <input
                type="text"
                className="form-control"
                placeholder="Buscar por email, código o materia..."
                value={busquedaRegistrado}
                onChange={(e) => setBusquedaRegistrado(e.target.value)}
              />
              <button
                type="button"
                className="btn btn-success text-nowrap"
                disabled={seleccionados.size === 0}
                onClick={handleDescargarReporte}
              >
                Descargar reporte ({seleccionados.size})
              </button>
            </div>
            {(() => {
              const filtro = busquedaRegistrado.trim().toLowerCase();
              const registradosFiltrados = filtro
                ? datos.registrados.filter(
                    (r) =>
                      r.email?.toLowerCase().includes(filtro) ||
                      r.materiaCode?.toLowerCase().includes(filtro) ||
                      r.materiaName?.toLowerCase().includes(filtro),
                  )
                : datos.registrados;

              const todosFiltradosSeleccionados =
                registradosFiltrados.length > 0 &&
                registradosFiltrados.every((r) => seleccionados.has(r.email));

              return (
                <div className="table-responsive" style={{ maxHeight: "420px", overflowY: "auto" }}>
                  <table className="table table-sm table-striped align-middle mb-0">
                    <thead style={{ position: "sticky", top: 0, zIndex: 1 }} className="table-light">
                      <tr>
                        <th>
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={todosFiltradosSeleccionados}
                            onChange={() => toggleSeleccionarTodo(registradosFiltrados)}
                            aria-label="Seleccionar todo lo filtrado"
                          />
                        </th>
                        <th>Email</th>
                        <th>ID Materia</th>
                        <th>Código</th>
                        <th>Materia</th>
                        <th>Categoría</th>
                        <th>Registro</th>
                        <th>Fecha de registro</th>
                      </tr>
                    </thead>
                    <tbody>
                      {registradosFiltrados.map((r) => (
                        <tr key={r.email}>
                          <td>
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={seleccionados.has(r.email)}
                              onChange={() => toggleSeleccion(r.email)}
                              aria-label={`Seleccionar ${r.email}`}
                            />
                          </td>
                          <td>{r.email}</td>
                          <td>{r.idMat}</td>
                          <td>{r.materiaCode}</td>
                          <td>{r.materiaName}</td>
                          <td>{r.materiaCategory}</td>
                          <td>{r.idRegistro}</td>
                          <td>{restarSeisHoras(r.fechaRegistro) || "—"}</td>
                        </tr>
                      ))}
                      {registradosFiltrados.length === 0 && (
                        <tr>
                          <td colSpan={8} className="text-center text-muted">
                            No hay registros que coincidan con "{busquedaRegistrado}".
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              );
            })()}

            <h5 className="mt-4">Claves de desinscripción (tokens)</h5>
            <div className="row">
              <TarjetaMetrica etiqueta="Generadas en total" valor={datos.tokens.total} />
              <TarjetaMetrica etiqueta="Usadas" valor={datos.tokens.usados} />
              <TarjetaMetrica etiqueta="Sin usar" valor={datos.tokens.sinUsar} />
            </div>
            <div className="table-responsive">
              <table className="table table-sm table-striped align-middle">
                <thead>
                  <tr>
                    <th>Token</th>
                    <th>Creado por</th>
                    <th>Estado</th>
                    <th>Usado por</th>
                    <th>Fecha de uso</th>
                  </tr>
                </thead>
                <tbody>
                  {datos.tokens.detalle.map((t) => (
                    <tr key={t.token}>
                      <td>{t.token}</td>
                      <td>{t.creadoPor}</td>
                      <td>
                        {t.usado ? (
                          <span className="badge bg-secondary">Usado</span>
                        ) : (
                          <span className="badge bg-success">Disponible</span>
                        )}
                      </td>
                      <td>{t.usadoPor || "—"}</td>
                      <td>{restarSeisHoras(t.fechaUso) || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};

export default DashboardAdmin;
